import { useState } from 'react'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import type { LineStatusEntry } from '../components/stationPage/fetchLineStatuses'
import { lineStatusTone } from '../components/stationPage/lineStatusTone'
import styles from './LineStatusRow.module.css'

export interface LineStatusRowProps {
  /** `null` = nothing reported (Good service). */
  readonly status: LineStatusEntry | null
  /** Fired the first time a disrupted row is expanded. */
  readonly onExpand: () => void
}

/**
 * Line view status row — Figma LV/StatusRow (`9241:17419`): a 36 grey disc
 * with a 12 tone dot, the status, and "Live · TfL". Good service is plain
 * text; a disruption with a TfL reason expands inline to show it (legend
 * B·3).
 */
export function LineStatusRow({ status, onExpand }: LineStatusRowProps): React.ReactElement {
  const { t } = useTranslation('line-details')
  const [open, setOpen] = useState(false)
  const tone = lineStatusTone(status?.severity, status?.description)
  const label =
    tone.rawLabel ?? t(tone.labelKey === 'line_status_good' ? 'status_good' : 'status_disruption')
  const reason = tone.tone === 'good' ? undefined : status?.reason
  const content = (
    <>
      <span className={styles.icon} aria-hidden="true">
        <span className={styles.dot} data-tone={tone.tone} />
      </span>
      <span className={styles.label}>{label}</span>
      <span className={styles.source}>{t('status_source')}</span>
    </>
  )
  if (reason === undefined) {
    return (
      <div className={styles.row} data-testid="line-status-row" data-tone={tone.tone}>
        {content}
      </div>
    )
  }
  return (
    <div className={styles.block} data-testid="line-status-row" data-tone={tone.tone}>
      <button
        type="button"
        className={`${styles.row} ${styles.toggle}`}
        aria-expanded={open}
        onClick={() => {
          if (!open) onExpand()
          setOpen(!open)
        }}
      >
        {content}
      </button>
      {open && (
        <p className={styles.reason} data-testid="line-status-reason">
          {reason}
        </p>
      )}
    </div>
  )
}
